import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "../providers/supabase/supabase";
import { organizacaoAtiva } from "../providers/supabase/organizacao";
import { lembrarMarcaDoLogin } from "./marcaDoLogin";

/**
 * Marca e módulos da organização ativa (migration 279): a própria, ou a do
 * "Ver como" do super admin. A North tem todos os módulos e a marca de sempre;
 * escritório cliente vê a logo, a cor e só os módulos dele.
 */

export type Modulo =
  | "consorcio" | "lances" | "north_ia" | "mensagens" | "feed" | "campanhas"
  | "seguros" | "fluxo_caixa" | "api";

export interface OrganizacaoAtiva {
  id: string;
  nome: string;
  tipo: "north" | "cliente";
  modulos: Modulo[];
  logo_url: string | null;
  cor_primaria: string | null;
}

export const useOrganizacaoAtiva = () =>
  useQuery({
    queryKey: ["organizacao-ativa"],
    staleTime: 10 * 60_000,
    queryFn: async (): Promise<OrganizacaoAtiva | null> => {
      const id = await organizacaoAtiva();
      const { data, error } = await supabase
        .from("organizacoes" as never)
        .select("id, nome, tipo, modulos, logo_url, cor_primaria")
        .eq("id" as never, id as never)
        .maybeSingle();
      if (error) throw error;
      return (data as unknown as OrganizacaoAtiva) ?? null;
    },
  });

/**
 * Item de módulo aparece? Enquanto a organização não chega, não: escritório
 * cliente não pode ver, nem por um instante, o menu da North.
 */
export const temModulo = (org: OrganizacaoAtiva | null | undefined, modulo?: Modulo) =>
  !modulo || (!!org && (org.tipo === "north" || (org.modulos ?? []).includes(modulo)));

export const ehCliente = (org: OrganizacaoAtiva | null | undefined) => org?.tipo === "cliente";

/** "North Consórcio Servopa" vira "Consórcio Servopa" para o escritório. */
export const tituloParaOrganizacao = (titulo: string, org: OrganizacaoAtiva | null | undefined) =>
  ehCliente(org) ? titulo.replace(/^North\s+/, "") : titulo;

/** "Feed North" é da North; o escritório vê só "Feed". */
export const nomeDoFeed = (org: OrganizacaoAtiva | null | undefined) => (ehCliente(org) ? "Feed" : "Feed North");

const VARIAVEIS = ["--marinho", "--noite", "--ouro"] as const;

/**
 * Cor do escritório no lugar do marinho da North: barra superior, menu,
 * cabeçalhos e botões que usam bg-marinho/bg-premium. O filete dourado vira um
 * tom claro da própria cor.
 * Devolve como desfazer.
 */
export const aplicarCor = (cor: string) => {
  const raiz = document.documentElement.style;
  raiz.setProperty("--marinho", cor);
  raiz.setProperty("--noite", `color-mix(in srgb, ${cor} 60%, black)`);
  raiz.setProperty("--ouro", `color-mix(in srgb, ${cor} 35%, white)`);
  return () => VARIAVEIS.forEach((v) => raiz.removeProperty(v));
};

export const AplicarMarca = () => {
  const { data: org } = useOrganizacaoAtiva();
  const cliente = ehCliente(org);
  const cor = cliente ? org?.cor_primaria : null;
  useEffect(() => (cor ? aplicarCor(cor) : undefined), [cor]);
  // Título da aba e marca do próximo login neste navegador (291). O "Ver como"
  // do super admin não troca o login dele.
  useEffect(() => {
    if (!org) return;
    const titulo = document.title;
    if (cliente) document.title = org.nome;
    void Promise.resolve(supabase.rpc("minha_organizacao" as never)).then(({ data: minha }) => {
      const propria = cliente && minha === org.id;
      lembrarMarcaDoLogin(propria ? { nome: org.nome, logo_url: org.logo_url, cor_primaria: org.cor_primaria } : null);
    }, () => {});
    return () => {
      document.title = titulo;
    };
  }, [org, cliente]);
  return null;
};
